<template>
  <v-col class="pa-0">
    <!-- Breadcrumbs with Action Buttons -->
    <v-row class="ma-0 align-center">
      <v-col class="col-auto py-0">
        <bread-crumbs />
      </v-col>

      <v-spacer />
    </v-row>

    <!-- Compact Header Section with Inline Tabs (show for both dashboard and report view) -->
    <div class="discrepancy-view-header-compact mb-3">
      <v-toolbar
        flat
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3">
          <font-awesome-icon
            :icon="['fas', 'chart-line']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-if="showDiscrepancyTabBar"
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="discrepancy-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabsWithCounts"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              v-if="tab.icon"
              :icon="tab.icon"
              class="mr-2"
            />

            {{ tab.label }}

            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :class="`ml-2 tab-count-chip ${tab.count === 0 ? 'gray--text' : `${tab.chipColor || ''}--text`}`"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <div
          v-else
          class="flex-grow-1"
        />
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-1"
          >
            <search-field
              v-model="searchText"
              :placeholder="showDashboard ? 'Search across all networks ...' : 'Search text fields...'"
            />
          </v-col>

          <!-- Report View Toolbar Buttons -->
          <template v-if="!showDashboard">
            <!-- Data Tools (Group, Sort, Columns, Jump) with integrated Filter -->
            <v-col
              v-if="showFilterBar"
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                :items="items"
                :current-required-columns="currentRequiredColumns"
                :report-key="reportKey"
                :table-columns="tableColumns"
                @on-grouping-change="onGroupingChange"
                @on-sorting-change="onSortingChange"
              >
                <!-- FilterPanel - Unified responsive filtering component -->
                <template v-slot:filter-controls>
                  <filter-panel
                    v-model="localFilterData"
                    :fields="discrepancyFilterFields"
                    view-name="discrepancy"
                    title="Filter Line Items"
                    :show-saved-filters="false"
                  />
                </template>
              </tools-toolbar>
            </v-col>

            <v-spacer />

            <!-- Export Button -->
            <v-col
              v-if="showExportBar"
              cols="auto"
              class="py-1"
            >
              <v-menu
                :disabled="!showExportBar"
                offset-y
                nudge-bottom="6"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    depressed
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'circle-down']"
                      size="sm"
                      :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                    />
                    <span class="d-none d-md-inline">Export</span>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="item in exportOptions"
                    :key="item.key"
                    :disabled="!item.active"
                    @click="item.callback"
                  >
                    <v-list-item-title v-if="item.key !== 'xlsx'">
                      {{ item.text }}
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      <export-xlsx
                        :item="item"
                        :show-export-bar="showExportBar"
                      />
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </template>

          <!-- Dashboard View Toolbar Buttons -->
          <template v-if="showDashboard">
            <!-- Data Tools (Sort, Group, Columns) — self-managed, no tools store -->
            <v-col
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                self-managed
                :items="assignedNetworksForCards"
                :sort-options="dashboardSortOptions"
                :group-options="dashboardGroupOptions"
                :table-columns="dashboardColumns"
                :current-required-columns="dashboardRequiredColumns"
                view-context="Card Properties"
                @on-sorting-change="onDashboardSortingChange"
                @on-grouping-change="onDashboardGroupingChange"
                @on-columns-change="onDashboardColumnsChange"
              />
            </v-col>
          </template>
        </v-row>
      </div>
    </div>

    <!-- Jump to Group (contextual row) -->
    <jump-to-group
      :groups="showDashboard ? dashboardFilteredGroups : filteredGroups"
      class="ma-0 mb-1 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Main Content: Dashboard -->
    <v-row
      v-if="showDashboard"
      class="ma-0"
    >
      <v-col>
        <!-- Dashboard Insights Loading State -->
        <v-card
          v-if="loadingDashboardInsights && currentTab.id !== 'all'"
          class="pa-8 text-center mb-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <div class="mt-4 subtitle-1">
            {{ shouldFetchAssignedClientsOnly ? 'Loading assigned clients...' : 'Loading insights...' }}
          </div>
        </v-card>

        <!-- Client Cards (ReportList) - Always show for dashboard -->
        <report-list
          :search-text="searchText"
          :is-external-user="isUserExternal"
          :tab-configs="tabs"
          :all-discrepancy-data="allDiscrepancyData"
          :assigned-networks="sortedNetworksForCards"
          :current-tab-id="currentTab.id"
          :visible-columns="dashboardVisibleColumns"
          report-path="/reports/discrepancy"
        />
      </v-col>
    </v-row>

    <!-- Main Content: Report -->
    <v-row
      v-else
      class="ma-0"
    >
      <v-col>
        <data-table
          ref="table"
          :actions="actions"
          :data-groups="filteredGroups"
          :hide-toolbar-buttons="true"
        >
          <template v-slot:empty-state>
            <div>Selected publisher has no Discrepancy data.</div>
          </template>
        </data-table>

        <creatives-discrepancy-modal />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import api from 'src/store/api'
import filter from 'src/utils/filter'
import format from 'src/utils/format'
import gam from 'src/utils/gam'
import groupUtil from 'src/utils/group'
import logger from 'src/plugins/logger'
import transform from 'src/utils/transform'
import columns from '../store/utils/columns'
import columnUtils from '@/tools/store/utils/columns'
import summaries from '../store/utils/summaries'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutsGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Discrepancy',
  components: {
    BreadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    CreativesDiscrepancyModal: () => import('../components/modals/CreativesDiscrepancyModal'),
    DataTable: () => import('@/tools/components/table/DataTable'),
    exportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    filterPanel: () => import('@/tools/components/filter/FilterPanel.vue'),
    ReportList: () => import('@/dashboard/components/ReportList'),
    toolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
  },
  beforeRouteLeave(to, from, next) {
    // Remove the ?tab= query parameter when leaving this view
    // to prevent invalid tab IDs from persisting to other views
    if (from.query.tab) {
      const query = { ...from.query }

      delete query.tab

      this.$router.replace({ ...from, query }).catch((err) => {
        // Handle any errors that occur during the route replacement
        logger.console.error(['Error removing tab query parameter:', err])
      })
    }

    next()
  },
  data() {
    return {
      searchText: '',
      activeTab: 0,
      actions: [
        {
          event: 'delivery/openTabToLineItem',
          name: 'Go to Line Item',
        },
        {
          event: 'delivery/openTabToOrder',
          name: 'Go to Order',
        },
        {
          event: 'delivery/showCreativesDiscrepancyModal',
          name: 'Creatives Discrepancy',
        },
      ],
      // Tab configurations for discrepancy ranges
      tabs: [
        {
          id: 'all',
          label: 'All',
          icon: ['fas', 'chart-line'],
          chipColor: 'primary',
        },
        {
          id: 'high-positive',
          label: 'Over 10%',
          icon: ['fas', 'circle-exclamation'],
          chipColor: 'error',
          summaryId: 'HIGH_POSITIVE',
        },
        {
          id: 'low-positive',
          label: 'Between 1 and 10%',
          icon: ['fas', 'arrow-trend-up'],
          chipColor: 'warning',
          summaryId: 'BETWEEN_POSITIVE_ONE_AND_TEN',
        },
        {
          id: 'zero',
          label: '0% Discrepancy',
          icon: ['fas', 'circle-check'],
          chipColor: 'gray',
          summaryId: 'APPROXIMATELY_ZERO',
        },
        {
          id: 'low-negative',
          label: 'Between -1 and -10%',
          icon: ['fas', 'arrow-trend-down'],
          chipColor: 'success',
          summaryId: 'BETWEEN_NEGATIVE_ONE_AND_TEN',
        },
        {
          id: 'high-negative',
          label: 'Under -10%',
          icon: ['fas', 'circle-exclamation'],
          chipColor: 'babyblue',
          summaryId: 'HIGH_NEGATIVE',
        },
      ],

      // Dashboard tabs for cross-report insights
      dashboardTabs: [
        {
          id: 'all-networks',
          label: 'Networks',
          icon: ['fas', 'list'],
          chipColor: 'primary',
        },
      ],

      // Dashboard-specific data
      assignedClients: [],
      allNetworksData: [], // All networks loaded from API
      dashboardInsights: null,
      loadingDashboardInsights: false,
      isRefreshing: false,
      loadingPublishers: false,

      // Report mode data (component-level, replaces store currentItems/currentGroups)
      reportData: [],
      loadingReportData: false,

      // Component-level grouping state
      localGroupByProperties: [],

      // Component-level sorting state
      localSortingByKeys: [],
      localSortingByDirections: [],

      // Component-level filter state (for FilterPanel)
      localFilterData: {},
      localFilterKeys: [],

      // Dashboard-level sort/group/columns state (self-managed, no tools store)
      dashboardSortConfig: [],
      dashboardGroupByProperties: [],
      dashboardColumns: [
        { text: 'Billing Code', value: 'billingCode', visible: true },
        { text: 'Network', value: 'networkCode', visible: true },
        { text: 'Team ID', value: 'teamId', visible: true },
      ],
    }
  },
  computed: {
    ...mapAuthGetters([
      'isInternalUser',
      'currentUser',
      'discrepancyDashboardLayout',
    ]),
    ...mapLayoutsGetters([
      'showExportBar',
      'showFilterBar',
    ]),
    ...mapToolsGetters([
      'currentColumns',
      'currentGroupByProperties',
      'currentRequiredColumns',
      'reportKey',
      'showSummaries',
      'sortByDirections',
      'sortByKeys',
      'tableColumns',
    ]),
    ...mapToolsState({
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      selectedSearch: (state) => state.selectedSearch,
      showRows: (state) => state.showRows,
    }),
    // Component-level grouping options
    groupingOptions() {
      return [
        { text: 'Advertiser', value: 'advertiserName' },
        { text: 'End Date', value: 'endDate' },
        { text: 'Line Item', value: 'lineItemName' },
        { text: 'Order', value: 'orderName' },
        { text: 'Start Date', value: 'startDate' },
      ]
    },

    showDashboard() {
      // Dashboard mode is determined entirely by the URL — no dependency on selectedSearch.
      // /reports/discrepancy                    → dashboard
      // /reports/discrepancy?billingCode=X       → report view
      // /reports/discrepancy/BASS2011            → report view
      const hasBillingCodeParam = !!this.$route.query.billingCode
      const hasPathParams = !!this.$route.params.reportId
      return !hasBillingCodeParam && !hasPathParams
    },

    // User type and scoping for dashboard insights
    isUserExternal() {
      return !this.isInternalUser
    },

    currentPersonId() {
      return this.currentUser?.personId
    },

    shouldFetchAllNetworks() {
      return this.isUserExternal
    },

    shouldFetchAssignedClientsOnly() {
      return this.isInternalUser
    },

    // Determine which tabs to show
    activeTabs() {
      return this.showDashboard ? this.dashboardTabs : this.tabs
    },

    // 'no-tabs' hides the dashboard tab bar (single "Networks" tab content shown directly).
    showDiscrepancyTabBar() {
      if (!this.showDashboard) return true
      return this.discrepancyDashboardLayout !== 'no-tabs'
    },

    // Current tab configuration
    currentTab() {
      return this.activeTabs[this.activeTab] || this.activeTabs[0]
    },

    // Get all data for current view (component-level)
    items() {
      // In report mode, use component-level reportData
      if (this.reportData.length > 0) {
        return this.reportData
      }

      // Fallback: empty array (dashboard mode uses dashboardInsights)
      return []
    },

    // QuickFilterPanel computed properties (component-level filtering)
    discrepancyFilterFields() {
      const data = this.items || []

      // Helper to build unique sorted options from data
      const buildOptions = (key) => {
        const values = [...new Set(data.map((item) => item[key]).filter(Boolean))]
        return values.sort().map((value) => ({ text: value, value }))
      }

      return [
        {
          key: 'advertiserName',
          label: 'Advertiser',
          icon: ['fas', 'building'],
          items: buildOptions('advertiserName'),
          multiple: true,
        },
        {
          key: 'orderName',
          label: 'Order',
          icon: ['fas', 'file-lines'],
          items: buildOptions('orderName'),
          multiple: true,
        },
        {
          key: 'gamPriority',
          label: 'Priority',
          icon: ['fas', 'arrow-up-1-9'],
          items: buildOptions('gamPriority'),
          multiple: true,
        },
        {
          key: 'salespersonName',
          label: 'Salesperson',
          icon: ['fas', 'user-tie'],
          items: buildOptions('salespersonName'),
          multiple: true,
        },
        {
          key: 'traffickerName',
          label: 'Trafficker',
          icon: ['fas', 'user'],
          items: buildOptions('traffickerName'),
          multiple: true,
        },
        {
          key: 'isMissingCreatives',
          label: 'Missing Creatives',
          icon: ['fas', 'square-xmark'],
          items: [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ],
          multiple: true,
        },
      ]
    },

    discrepancyFilterValues() {
      // Return localFilterData directly - all fields are multi-select arrays
      return this.localFilterData || {}
    },

    activeFilterCount() {
      return this.localFilterKeys.length
    },

    // Filter items based on active tab using summary configurations
    tabItems() {
      const currentTabId = this.currentTab.id

      // If "All" tab, return everything
      if (currentTabId === 'all') {
        return this.items
      }

      // Get the summary config for this tab
      const { summaryId } = this.currentTab

      if (!summaryId) {
        return this.items
      }

      const discrepancySummaries = summaries?.discrepancy
      const summaryConfig = discrepancySummaries?.[summaryId]

      if (!summaryConfig || !summaryConfig.parameters) {
        return this.items
      }

      // Convert summary parameters to filter format
      const filterData = {}
      const paramKeys = Object.keys(summaryConfig.parameters)

      paramKeys.forEach((key) => {
        const paramValue = summaryConfig.parameters[key]
        filterData[key] = typeof paramValue === 'function' ? paramValue() : paramValue
      })

      const filterKeys = Object.keys(filterData)

      // If no filters, return all data
      if (filterKeys.length === 0) {
        return this.items
      }

      // Apply filters to get tab-specific items
      const reportFilter = filter.report.bind(filter)
      const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

      return this.items.filter(isItemIncluded)
    },

    // Get available networks for dashboard
    availableNetworks() {
      // Use allNetworksData from API (like Pacing does)
      // This ensures data is available when navigating directly to dashboard
      return this.allNetworksData || []
    },

    // Format networks for card display with proper search option properties
    assignedNetworksForCards() {
      if (!this.showDashboard) {
        return []
      }

      return this.availableNetworks.map((network) => ({
        text: network.teamId === -1 ? network.publisherName : `${network.publisherName}: ${network.teamName}`,

        billingCode: network.billingCode,
        networkCode: network.networkCode,

        publisherName: network.publisherName,
        publisherId: network.publisherId,
        teamId: network.teamId,
        teamCode: network.teamCode || network.teamId,

        // Pass through loading state
        loadingClientData: network.loadingClientData,
        loadingTeams: network.loadingTeams,

        // Search option properties for navigation
        isSearchOption: true,
        route: 'reports',
        reportKey: 'discrepancy',
        reportId: network.billingCode,
        subId: String(network.teamId),
        reportName: 'discrepancy',
        reportPath: 'discrepancy',
      }))
    },

    // Dashboard sort options (self-managed, no tools store)
    dashboardSortOptions() {
      return [
        { text: 'Name', value: 'text' },
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    // Dashboard group options (self-managed, no tools store)
    dashboardGroupOptions() {
      return [
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    // Required columns for dashboard cards (cannot be hidden)
    dashboardRequiredColumns() {
      return []
    },

    // Visible column values for dashboard cards (passed to ReportList → EnhancedNetworkCard)
    dashboardVisibleColumns() {
      return this.dashboardColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.value)
    },

    // Dashboard filtered groups (for Jump button)
    dashboardFilteredGroups() {
      if (!this.showDashboard || this.dashboardGroupByProperties.length === 0) return []

      const cards = this.assignedNetworksForCards || []
      if (cards.length === 0) return []

      const groupKey = this.dashboardGroupByProperties[0]
      const groupMap = {}

      cards.forEach((card) => {
        const value = card[groupKey] || 'Unknown'
        if (!groupMap[value]) {
          groupMap[value] = { groupName: value, labels: [value], items: [] }
        }
        groupMap[value].items.push(card)
      })

      return Object.values(groupMap).sort((a, b) => a.groupName.localeCompare(b.groupName))
    },

    // Sorted network cards for dashboard
    sortedNetworksForCards() {
      const cards = [...(this.assignedNetworksForCards || [])]
      if (cards.length === 0) return cards

      // Apply sorting
      if (this.dashboardSortConfig.length > 0) {
        cards.sort((a, b) => this.dashboardSortConfig.reduce((result, sort) => {
          if (result !== 0) return result
          const dir = sort.direction === 'desc' ? -1 : 1
          const va = a[sort.column]
          const vb = b[sort.column]

          // Handle numbers
          if (typeof va === 'number' && typeof vb === 'number') {
            return (va - vb) * dir
          }

          // Handle strings
          return String(va || '').localeCompare(String(vb || '')) * dir
        }, 0))
      }

      return cards
    },

    // Calculate tab counts dynamically using summary configurations
    tabsWithCounts() {
      // For dashboard, show count of available networks
      if (this.showDashboard) {
        const networkCount = this.availableNetworks.length

        return this.dashboardTabs.map((tab) => ({
          ...tab,
          count: networkCount,
        }))
      }

      const discrepancySummaries = summaries?.discrepancy

      if (!discrepancySummaries) {
        return this.tabs // Return static tabs if summaries not loaded
      }

      // Calculate counts for each tab
      return this.tabs.map((tab) => {
        if (tab.id === 'all') {
          return {
            ...tab,
            count: this.items.length,
          }
        }

        const { summaryId } = tab
        if (!summaryId) {
          return tab
        }

        const summaryConfig = discrepancySummaries[summaryId]
        const count = this.calculateSummaryCount(this.items, summaryConfig)

        return {
          ...tab,
          count,
        }
      })
    },

    // Apply user filters (from QuickFilterPanel) and search filter on top of tab filter
    filteredItems() {
      let items = this.tabItems

      // Apply user-selected filters from QuickFilterPanel (if any)
      if (this.localFilterKeys.length > 0) {
        items = items.filter((item) => this.localFilterKeys.every((key) => {
          // Check every active filter
          const filterValue = this.localFilterData[key]
          const itemValue = item[key]

          // If filter value is an array (multi-select), check if item value is in array
          if (Array.isArray(filterValue)) {
            if (filterValue.length === 0) return true
            // Handle case where item value might also be an array
            if (Array.isArray(itemValue)) {
              return filterValue.some((fv) => itemValue.includes(fv))
            }
            // Check if item value is in filter array (case-insensitive for strings)
            return filterValue.some((fv) => {
              if (typeof fv === 'string' && typeof itemValue === 'string') {
                return fv.toLowerCase() === itemValue.toLowerCase()
              }
              return fv === itemValue
            })
          }

          // Single value comparison (case-insensitive for strings)
          if (typeof filterValue === 'string' && typeof itemValue === 'string') {
            return filterValue.toLowerCase() === itemValue.toLowerCase()
          }

          return filterValue === itemValue
        }))
      }

      // Apply search text filter
      if (!this.searchText || this.searchText.trim() === '') {
        return items
      }

      const searchLower = this.searchText.toLowerCase().trim()

      return items.filter((item) => {
        // Search across key fields
        const lineItemName = (item.lineItemName || '').toLowerCase()
        const orderName = (item.orderName || '').toLowerCase()
        const advertiserName = (item.advertiserName || '').toLowerCase()
        const status = (item.status || '').toLowerCase()

        return lineItemName.includes(searchLower)
          || orderName.includes(searchLower)
          || advertiserName.includes(searchLower)
          || status.includes(searchLower)
      })
    },

    // Apply sorting to filtered items
    sortedItems() {
      const items = this.filteredItems

      if (!items || items.length === 0) {
        return items
      }

      const sortKeys = this.localSortingByKeys
      const sortDirections = this.localSortingByDirections

      // If no sorting configured, return items unchanged
      if (!sortKeys || sortKeys.length === 0) {
        return items
      }

      // Create a copy to avoid mutating original array
      const sortedItems = [...items]

      // Sort using multi-level comparison
      sortedItems.sort((a, b) => {
        let result = 0
        let i = 0

        while (result === 0 && i < sortKeys.length) {
          const key = sortKeys[i]
          // Direction is boolean: false=asc, true=desc
          const isDesc = sortDirections[i] === true

          // Get values for comparison
          let valueA = a[key]
          let valueB = b[key]

          // Handle null/undefined values (push to end)
          if (valueA == null && valueB != null) {
            result = 1
          } else if (valueA != null && valueB == null) {
            result = -1
          } else if (valueA != null && valueB != null) {
            // Convert to comparable format
            if (typeof valueA === 'string') valueA = valueA.toLowerCase()
            if (typeof valueB === 'string') valueB = valueB.toLowerCase()

            // Compare values
            if (valueA < valueB) result = -1
            else if (valueA > valueB) result = 1
          }

          // Apply sort direction if there's a difference
          if (result !== 0 && isDesc) {
            result = -result
          }

          i += 1
        }

        return result
      })

      return sortedItems
    },

    // Format items as groups for DataTable component
    filteredGroups() {
      // Only return groups when showing a report (not dashboard)
      if (this.showDashboard) {
        return []
      }

      // Don't override if we don't have data yet
      if (!this.sortedItems || this.sortedItems.length === 0) {
        return [{
          groupName: null,
          labels: ['Line Items'],
          items: [],
          isPartOfGroup: false,
          isEmpty: true,
        }]
      }

      // Component-level grouping: Use local grouping state
      if (this.localGroupByProperties && this.localGroupByProperties.length > 0) {
        // Group the sorted items by the selected properties
        const groups = groupUtil.byProperty(this.sortedItems, this.localGroupByProperties)

        // Add isEmpty flag and convert to expected format for DataTable
        return groups.map((grp) => ({
          ...grp,
          key: grp.groupName,
          name: grp.labels.join(' :: '),
          isEmpty: grp.items.length === 0,
        }))
      }

      // Default: single ungrouped list
      return [{
        key: 'all',
        name: 'All Items',
        groupName: null,
        labels: ['Line Items'],
        items: this.sortedItems || [],
        isPartOfGroup: false,
        isEmpty: false,
      }]
    },

    // All discrepancy data for passing to dashboard cards
    allDiscrepancyData() {
      // On dashboard: use data from dashboardInsights
      if (this.showDashboard) {
        return this.dashboardInsights?.discrepancyData || []
      }

      // In report view: use current report data
      return this.items
    },

    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },
  },
  watch: {
    // Update URL when active tab changes
    activeTab(newTab, oldTab) {
      if (newTab !== oldTab) {
        const tabId = this.tabs[newTab]?.id
        this.updateUrlWithTab(tabId)
      }
    },

    // Sync activeTab with URL on route change
    '$route.query.tab': {
      immediate: true,
      handler(newTab) {
        if (newTab) {
          const tabIndex = this.tabs.findIndex((tab) => tab.id === newTab)
          if (tabIndex !== -1 && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      },
    },

    // Watch for billingCode query param changes (from dashboard card clicks)
    // This handles component reuse when navigating via query params
    '$route.query.billingCode': {
      async handler(newBillingCode, oldBillingCode) {
        if (!newBillingCode) return
        if (oldBillingCode === newBillingCode) return

        // Wait for next tick to give SearchBar time to update selectedSearch
        await this.$nextTick()

        // Get publisherId from selectedSearch OR look up from dashboard insights
        let publisherId = this.selectedSearch?.publisherId || this.selectedSearch?.networkCode
        let publisherName = this.selectedSearch?.text || this.selectedSearch?.publisherName || newBillingCode

        // If selectedSearch doesn't have publisherId yet, try to find it from dashboard data
        if (!publisherId) {
          // Check enrichedClients directly (not assignedNetworksForCards which returns [] when not in dashboard mode)
          if (this.dashboardInsights?.enrichedClients) {
            const enrichedClient = this.dashboardInsights.enrichedClients.find(
              (ec) => ec.billingCode === newBillingCode,
            )
            if (enrichedClient) {
              publisherId = enrichedClient.publisherId || enrichedClient.networkCode
              publisherName = enrichedClient.publisherName || enrichedClient.clientName || enrichedClient.text || newBillingCode
              logger.console.info(['[Discrepancy] Found publisherId from enrichedClients:', publisherId, 'publisherName:', publisherName])
            }
          }

          // Also check allNetworksData for "All Networks" tab
          if (!publisherId && this.allNetworksData) {
            const allNetwork = this.allNetworksData.find(
              (n) => n.billingCode === newBillingCode || n.reportId === newBillingCode,
            )
            if (allNetwork) {
              publisherId = allNetwork.publisherId || allNetwork.networkCode
              publisherName = allNetwork.publisherName || allNetwork.clientName || allNetwork.text || newBillingCode
              logger.console.info(['[Discrepancy] Found publisherId from allNetworksData:', publisherId, 'publisherName:', publisherName])
            }
          }
        }

        // If still no publisherId, look up via API (needed when navigating directly to URL)
        if (!publisherId) {
          logger.console.info(['[Discrepancy] Looking up publisherId from API for billingCode:', newBillingCode])
          try {
            const publisherResponse = await api.getPublishersByBillingCodes(newBillingCode)
            const publisher = publisherResponse?.content?.[0]
            if (publisher) {
              publisherId = publisher.publisherId || publisher.networkCode
              publisherName = publisher.publisherName || publisher.clientName || newBillingCode
              logger.console.info(['[Discrepancy] Found publisherId from API:', publisherId, 'publisherName:', publisherName])
            }
          } catch (err) {
            logger.console.error(['[Discrepancy] Error looking up publisherId from API:', err])
          }
        }

        // Final fallback - use billingCode (will likely return empty results)
        if (!publisherId) {
          logger.console.warn(['[Discrepancy] No publisherId found anywhere, falling back to billingCode:', newBillingCode])
          publisherId = newBillingCode
        }

        const teamId = this.$route.query.teamId || this.selectedSearch?.teamId || '-1'

        // Reset tab to first tab when navigating to report view
        this.activeTab = 0

        // Commit selectedSearch with proper text for SearchBar display
        const selectedSearch = {
          isSearchOption: true,
          route: 'reports',
          reportKey: 'discrepancy',
          reportId: newBillingCode,
          billingCode: newBillingCode,
          subId: teamId,
          teamId,
          reportName: 'discrepancy',
          reportPath: 'discrepancy',
          publisherId,
          publisherName,
          text: publisherName,
        }
        this.$store.commit('tools/selectedSearch', selectedSearch)

        logger.console.info([`[Discrepancy] billingCode query param changed, fetching data for publisherId ${publisherId}, team ${teamId}`])

        this.loadingReportData = true
        this.reportData = []

        try {
          const data = await this.fetchNetworkDiscrepancyData(publisherId, teamId)
          this.reportData = data
          logger.console.info([`[Discrepancy] Fetched ${data.length} items via billingCode watcher`])
        } finally {
          this.loadingReportData = false
        }
      },
    },

    // Fetch dashboard insights when entering dashboard mode, clear report data when leaving report mode
    showDashboard: {
      immediate: true,
      handler(isDashboard) {
        logger.console.info(['=== showDashboard watcher triggered ===', isDashboard])
        if (isDashboard) {
          // Entering dashboard mode — clear stale report data and fetch insights
          this.reportData = []
          this.fetchDashboardInsights()
        }
      },
    },

    // Fetch report data when selectedSearch changes (non-dashboard mode)
    selectedSearch: {
      immediate: true,
      async handler(newSelection, oldSelection) {
        // Skip if in dashboard mode — handled by showDashboard watcher
        if (this.showDashboard) return

        // Skip if no valid selection
        if (!newSelection) return

        // Skip if billingCode query param is present - that's handled by created() or billingCode watcher
        if (this.$route.query.billingCode) return

        // Skip duplicate calls for report mode
        if (oldSelection
            && newSelection.reportId === oldSelection.reportId
            && newSelection.subId === oldSelection.subId) {
          return
        }

        // Fetch discrepancy data for the selected client/team (component-level)
        await this.fetchReportData()
      },
    },

    // Sync localFilterData changes to localFilterKeys (for FilterPanel)
    localFilterData: {
      deep: true,
      handler(newData) {
        this.localFilterKeys = Object.keys(newData || {})
      },
    },

    // Sync local grouping with store's currentGroupByProperties
    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    // Sync local sorting with store's sortByKeys
    sortByKeys: {
      immediate: true,
      handler(newSortKeys) {
        if (newSortKeys && Array.isArray(newSortKeys)) {
          this.localSortingByKeys = [...newSortKeys]
        }
      },
    },

    // Sync local sorting directions with store's sortByDirections
    sortByDirections: {
      immediate: true,
      handler(newSortDirections) {
        if (newSortDirections && Array.isArray(newSortDirections)) {
          this.localSortingByDirections = [...newSortDirections]
        }
      },
    },
  },
  async created() {
    // Self-managed view: Set reportName in tools store
    // This is normally done by SearchBar, but we bypass that for self-managed views
    this.$store.commit('tools/reportName', 'discrepancy')

    // Initialize columns for ToolsDrawer (sort, filter, columns tabs)
    // This is normally done by SearchBar via setColumns action, but we bypass that for self-managed views
    const existingDiscrepancyColumns = this.$store.state.tools.columns.discrepancy

    if (!columnUtils.hasCompatibleColumnSchema(existingDiscrepancyColumns, columns.discrepancy)) {
      const storedPrefs = columnUtils.loadColumnPreferences('discrepancy')
      const mergedColumns = columnUtils.applyColumnPreferences(columns.discrepancy, storedPrefs)
      this.$store.commit('tools/columns', { discrepancy: mergedColumns })
    }

    // Derive valid sort keys from columns.js (sortable columns only)
    const validDiscrepancyKeys = columns.discrepancy
      .filter((col) => col.sortable)
      .map((col) => col.key)

    // Initialize default sorting by advertiser then order name (ascending) if not set or if current sort key is invalid for this view
    const currentSortKeys = this.$store.state.tools.sortingByKeys || []
    const hasValidSortKey = currentSortKeys.length > 0 && currentSortKeys.every((key) => validDiscrepancyKeys.includes(key))

    if (!hasValidSortKey) {
      this.$store.commit('tools/sortingByKeys', ['advertiserName', 'orderName'])
      this.$store.commit('tools/sortingByDirections', ['asc', 'asc'])
    }

    // Initialize tab from URL on component creation
    const tabParam = this.$route.query.tab
    if (tabParam) {
      const tabIndex = this.tabs.findIndex((tab) => tab.id === tabParam)
      if (tabIndex !== -1) {
        this.activeTab = tabIndex
      }
    }

    // Check if navigating to specific network via query params (e.g., /reports/discrepancy?billingCode=BASS2011&teamId=-1)
    const { billingCode: queryBillingCode, teamId: queryTeamId } = this.$route.query

    if (queryBillingCode) {
      logger.console.info(['[Discrepancy] Navigated with query params, fetching network data', { billingCode: queryBillingCode, teamId: queryTeamId }])

      // Look up publisherId from billingCode via API
      let publisherId = null
      let publisherName = queryBillingCode
      try {
        const publisherResponse = await api.getPublishersByBillingCodes(queryBillingCode)
        const publisher = publisherResponse?.content?.[0]
        if (publisher) {
          publisherId = publisher.publisherId || publisher.networkCode
          publisherName = publisher.publisherName || publisher.clientName || queryBillingCode
          logger.console.info(['[Discrepancy] created() - Found publisherId from API (query params):', publisherId])
        }
      } catch (err) {
        logger.console.error(['[Discrepancy] created() - Error looking up publisherId:', err])
      }

      // Fallback to billingCode if API lookup failed
      if (!publisherId) {
        logger.console.warn(['[Discrepancy] created() - No publisherId found, falling back to billingCode:', queryBillingCode])
        publisherId = queryBillingCode
      }

      // Set selectedSearch so SearchBar and BreadCrumbs display the correct selection
      const selectedSearch = {
        isSearchOption: true,
        route: 'reports',
        reportKey: 'discrepancy',
        reportId: queryBillingCode,
        billingCode: queryBillingCode,
        subId: queryTeamId || '-1',
        teamId: queryTeamId || '-1',
        reportName: 'discrepancy',
        reportPath: 'discrepancy',
        publisherId,
        publisherName,
        text: publisherName,
      }
      this.$store.commit('tools/selectedSearch', selectedSearch)

      // Fetch data for this specific network and store in reportData (component-level)
      const data = await this.fetchNetworkDiscrepancyData(publisherId, queryTeamId || '-1')
      this.reportData = data
      logger.console.info([`[Discrepancy] Fetched ${data.length} line items for network ${queryBillingCode} (publisherId: ${publisherId}, query params)`])
      return
    }

    // Check if navigating to specific network via path params (e.g., /reports/discrepancy/BASS2011/-1)
    const { reportId: pathBillingCode, subId: pathTeamId } = this.$route.params

    if (pathBillingCode) {
      logger.console.info(['[Discrepancy] Navigated with path params, fetching network data', { billingCode: pathBillingCode, teamId: pathTeamId }])

      // Look up publisherId from billingCode via API
      let publisherId = null
      let publisherName = pathBillingCode
      try {
        const publisherResponse = await api.getPublishersByBillingCodes(pathBillingCode)
        const publisher = publisherResponse?.content?.[0]
        if (publisher) {
          publisherId = publisher.publisherId || publisher.networkCode
          publisherName = publisher.publisherName || publisher.clientName || pathBillingCode
          logger.console.info(['[Discrepancy] created() - Found publisherId from API:', publisherId])
        }
      } catch (err) {
        logger.console.error(['[Discrepancy] created() - Error looking up publisherId:', err])
      }

      // Fallback to billingCode if API lookup failed
      if (!publisherId) {
        logger.console.warn(['[Discrepancy] created() - No publisherId found, falling back to billingCode:', pathBillingCode])
        publisherId = pathBillingCode
      }

      // Set selectedSearch so SearchBar displays the correct selection
      const selectedSearch = {
        isSearchOption: true,
        route: 'reports',
        reportKey: 'discrepancy',
        reportId: pathBillingCode,
        billingCode: pathBillingCode,
        subId: pathTeamId || '-1',
        teamId: pathTeamId || '-1',
        reportName: 'discrepancy',
        reportPath: 'discrepancy',
        publisherId,
        publisherName,
        text: publisherName,
      }
      this.$store.commit('tools/selectedSearch', selectedSearch)

      // Fetch data for this specific network and store in reportData (component-level)
      const data = await this.fetchNetworkDiscrepancyData(publisherId, pathTeamId || '-1')
      this.reportData = data
      logger.console.info([`[Discrepancy] Fetched ${data.length} line items for network ${pathBillingCode} (publisherId: ${publisherId}, path params)`])
    }

    // Dashboard insights are fetched by the selectedSearch watcher (immediate: true, checks isDashboard)
    // No need to call here - that would cause duplicate API calls
  },
  methods: {
    /**
     * Fetch report data for the selected network (component-level data management)
     * Called when navigating to a specific network report view
     */
    async fetchReportData() {
      const { selectedSearch } = this

      // Get network identifier - selectedSearch may have publisherId, billingCode, or reportId
      // depending on how we navigated to this view
      const networkId = selectedSearch?.publisherId || selectedSearch?.billingCode || selectedSearch?.reportId

      if (!selectedSearch || !networkId) {
        logger.console.warn(['[Discrepancy] fetchReportData called without valid selectedSearch:', selectedSearch])
        return
      }

      // teamId may be under .teamId or .subId depending on the source
      const teamId = selectedSearch.teamId || selectedSearch.subId || '-1'

      this.loadingReportData = true
      this.reportData = []

      try {
        logger.console.info([`[Discrepancy] fetchReportData - Fetching data for network ${networkId}, team ${teamId || 'all'}`])

        const data = await this.fetchNetworkDiscrepancyData(networkId, teamId || '-1')

        this.reportData = data

        logger.console.info([`[Discrepancy] fetchReportData - Fetched ${data.length} line items`])
      } catch (error) {
        logger.console.error(['[Discrepancy] fetchReportData - Error:', error])

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error loading discrepancy data: ${error.message}`,
          status: 'error',
        }, { root: true })
      } finally {
        this.loadingReportData = false
      }
    },

    /**
     * Fetch dashboard insights based on user type
     * External users: fetch all networks (limited scope)
     * Internal users: fetch only assigned clients
     */
    async fetchDashboardInsights() {
      // Always fetch networks data from API first
      await this.fetchAllNetworksData()

      if (this.shouldFetchAllNetworks) {
        return this.fetchAllNetworkInsights()
      }
      return this.fetchAssignedClientInsights()
    },

    /**
     * Fetch all networks from API (publishers + teams)
     * Similar to Pacing.vue pattern - direct API call instead of relying on searchResults
     * Only includes publishers with hasDiscrepancy=true (filtered at API level)
     */
    async fetchAllNetworksData() {
      try {
        this.loadingPublishers = true
        logger.console.info(['Fetching all networks from API for Discrepancy dashboard'])

        // Fetch only publishers with discrepancy enabled (API-level filtering)
        const publishersResponse = await api.getPublishersWithDiscrepancy()
        const publishers = publishersResponse?.content || []

        logger.console.info([`Loaded ${publishers.length} publishers with hasDiscrepancy=true`])

        // Create placeholder cards for immediate display
        const publisherPlaceholders = publishers.map((pub) => ({
          publisherId: pub.publisherId,
          networkCode: pub.publisherId,
          publisherName: pub.name,
          billingCode: pub.billingCode,
          teamId: -1,
          teamCode: -1,
          teamName: 'All Teams',
          loadingTeams: true,
          loadingClientData: true,
        }))

        // Set initial data for quick display
        this.allNetworksData = publisherPlaceholders
        this.loadingPublishers = false

        // Fetch teams for each publisher in parallel
        const teamsPromises = publishers.map(async (pub) => {
          try {
            const teamsResponse = await api.getTeams(pub.publisherId)
            return { publisher: pub, teams: teamsResponse?.content || [] }
          } catch (err) {
            logger.console.error([`Error fetching teams for publisher ${pub.publisherId}:`, err])
            return { publisher: pub, teams: [] }
          }
        })

        const publishersWithTeams = await Promise.all(teamsPromises)

        // Expand into publisher-team combinations
        const networkCards = []
        publishersWithTeams.forEach(({ publisher: pub, teams }) => {
          const hasTeamsEnabled = pub.hasTeam === true

          if (hasTeamsEnabled && teams.length > 0) {
            // Create a card for each team
            teams.forEach((team) => {
              networkCards.push({
                publisherId: pub.publisherId,
                networkCode: pub.publisherId,
                publisherName: pub.name,
                billingCode: pub.billingCode,
                teamId: team.teamId,
                teamCode: team.teamCode || team.teamId,
                teamName: team.teamName || team.name,
                loadingTeams: false,
                loadingClientData: false,
              })
            })
          } else {
            // No teams or teams disabled - single card for publisher
            networkCards.push({
              publisherId: pub.publisherId,
              networkCode: pub.publisherId,
              publisherName: pub.name,
              billingCode: pub.billingCode,
              teamId: -1,
              teamCode: -1,
              teamName: 'All Teams',
              loadingTeams: false,
              loadingClientData: false,
            })
          }
        })

        this.allNetworksData = networkCards
        logger.console.info([`Built ${networkCards.length} network cards for Discrepancy dashboard`])
      } catch (err) {
        logger.console.error(['Error in fetchAllNetworksData:', err])
        this.allNetworksData = []
      } finally {
        this.loadingPublishers = false
      }
    },

    /**
     * Fetch insights for internal users based on their assigned clients
     */
    async fetchAssignedClientInsights() {
      this.loadingDashboardInsights = true

      try {
        // 1. Get assigned clients for this person
        const response = await api.getClientAssignments(this.currentPersonId)
        const { content, error } = response || {}

        if (error) {
          logger.console.error(['Error fetching client assignments:', error])
          this.assignedClients = []
          return
        }

        this.assignedClients = content || []

        logger.console.info([
          `Fetched ${this.assignedClients.length} assigned clients for personId: ${this.currentPersonId}`,
        ])

        // 2. Extract billing codes for fetching insights
        const billingCodes = this.assignedClients.map((a) => a.billingCode).filter(Boolean)

        logger.console.info(['Assigned client billing codes:', billingCodes])

        // 3. Get networks for assigned clients from allNetworksData (already loaded)
        const assignedNetworks = this.allNetworksData.filter((network) => billingCodes.includes(network.billingCode))

        // 4. Fetch discrepancy data for assigned networks
        const discrepancyDataPromises = assignedNetworks.map((network) => this.fetchNetworkDiscrepancyData(network.networkCode, network.teamId))

        const discrepancyDataArrays = await Promise.all(discrepancyDataPromises)

        // Flatten all discrepancy data into single array
        const allDiscrepancyData = discrepancyDataArrays.flat()

        this.dashboardInsights = {
          assignedClients: this.assignedClients,
          billingCodes,
          discrepancyData: allDiscrepancyData,
        }

        logger.console.info([`Fetched discrepancy data for ${assignedNetworks.length} assigned networks, ${allDiscrepancyData.length} total line items`])
      } catch (err) {
        logger.console.error(['Error in fetchAssignedClientInsights:', err])
      } finally {
        this.loadingDashboardInsights = false
      }
    },

    /**
     * Fetch insights for external users across all their networks
     */
    async fetchAllNetworkInsights() {
      this.loadingDashboardInsights = true

      try {
        logger.console.info(['Fetching all network insights for external user'])

        // Use allNetworksData which was already loaded by fetchAllNetworksData
        const networks = this.allNetworksData || []

        // Fetch discrepancy data for each network
        const discrepancyDataPromises = networks.map((network) => this.fetchNetworkDiscrepancyData(network.networkCode, network.teamId))

        const discrepancyDataArrays = await Promise.all(discrepancyDataPromises)

        // Flatten all discrepancy data into single array
        const allDiscrepancyData = discrepancyDataArrays.flat()

        this.dashboardInsights = {
          scope: 'all-networks',
          discrepancyData: allDiscrepancyData,
        }

        logger.console.info([`Fetched discrepancy data for ${networks.length} networks, ${allDiscrepancyData.length} total line items`])
      } catch (err) {
        logger.console.error(['Error in fetchAllNetworkInsights:', err])
      } finally {
        this.loadingDashboardInsights = false
      }
    },

    /**
     * Refresh all data (called by Refresh button)
     */
    async refreshData() {
      this.isRefreshing = true
      try {
        if (this.showDashboard) {
          await this.fetchAllNetworksData()
        } else {
          await this.fetchReportData()
        }
      } finally {
        this.isRefreshing = false
      }
    },

    /**
     * Fetch discrepancy data for a specific network
     * @param {string} publisherId - Network publisher ID
     * @param {string} teamId - Team ID
     * @returns {Promise<Array>} Array of discrepancy line items
     */
    async fetchNetworkDiscrepancyData(publisherId, teamId) {
      try {
        const response = await api.getDiscrepancyDataForNetwork({
          // Don't pass limit to fetch all items (limit=0 causes Range errors)
          publisherId,
          teamId: teamId !== '-1' ? teamId : undefined,
        })

        // Handle error responses or malformed data
        if (!response || response.message || response.error) {
          const errorMsg = response?.message || response?.error || 'Unknown error'
          logger.console.error([`Error fetching discrepancy data for network ${publisherId}:`, errorMsg])
          return []
        }

        const { content, error } = response

        if (error) {
          logger.console.error([`Error fetching discrepancy data for network ${publisherId}:`, error])
          return []
        }

        // Apply field mappings matching transform.discrepancy() so columns render correctly.
        // The API returns fields like gamPriority, goal, and monetary values in micros
        // which need to be mapped/converted for the column definitions.
        const mapItem = (item) => {
          const mappedItem = { ...item }
          const { networkCode } = mappedItem

          mappedItem.allRevenue = (mappedItem.allRevenue || 0) / 1000000
          mappedItem.booked = mappedItem.goal
          mappedItem.potentialLostRevenue = (mappedItem.potentialLostRevenue || 0) / 1000000
          mappedItem.priority = mappedItem.gamPriority
          mappedItem.rate = (mappedItem.rate || 0) / 1000000
          mappedItem.osi = transform.getOsiView(mappedItem.type, mappedItem.booked, mappedItem.osi)

          if (networkCode) {
            mappedItem.lineItemGamLink = gam.getLineItemUrl(networkCode, mappedItem.lineItemCode)
            mappedItem.orderGamLink = gam.getOrderUrl(networkCode, mappedItem.orderCode)
          }

          return mappedItem
        }

        return (content || []).map(mapItem)
      } catch (err) {
        logger.console.error([`Exception fetching discrepancy data for network ${publisherId}:`, err])
        return []
      }
    },

    numberFormatter(value) {
      return format.number(value)
    },

    /**
     * Calculate the count of items matching a summary configuration
     * @param {Array} data - Array of items to filter
     * @param {Object} summaryConfig - Summary configuration with parameters
     * @returns {Number} Count of matching items
     */
    calculateSummaryCount(data, summaryConfig) {
      if (!data || !data.length || !summaryConfig || !summaryConfig.parameters) {
        return 0
      }

      // Convert summary parameters to filter format
      const filterData = {}
      const paramKeys = Object.keys(summaryConfig.parameters)

      paramKeys.forEach((key) => {
        const paramValue = summaryConfig.parameters[key]
        filterData[key] = typeof paramValue === 'function' ? paramValue() : paramValue
      })

      const filterKeys = Object.keys(filterData)

      // If no filters, return all data count
      if (filterKeys.length === 0) {
        return data.length
      }

      // Apply filters to get matching items
      const reportFilter = filter.report.bind(filter)
      const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)
      const matchingItems = data.filter(isItemIncluded)

      return matchingItems.length
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Discrepancy component
      const validParameters = ['tab', 'publisher', 'team', 'startDate', 'endDate', 'lineItem', 'order', 'discrepancy', 'billingCode', 'teamId', 'networkCode', 'teamCode']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    /**
     * Update URL with tab parameter for bookmarkable states
     * @param {String} tabId - Tab identifier (all, zero, low-positive, etc.)
     */
    updateUrlWithTab(tabId) {
      const currentQuery = this.$route.query || {}
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // For "all" tab, remove the tab parameter to keep URL clean
      if (tabId === 'all') {
        const { tab, ...rest } = cleanedQuery

        const currentQueryString = JSON.stringify(currentQuery)
        const newQueryString = JSON.stringify(rest)

        if (currentQueryString !== newQueryString) {
          this.$router.push({ query: rest })
        }
        return
      }

      // Add or update tab parameter for other tabs
      const newQuery = {
        ...cleanedQuery,
        tab: tabId,
      }

      // Only update route if query actually changed
      const currentQueryString = JSON.stringify(currentQuery)
      const newQueryString = JSON.stringify(newQuery)

      if (currentQueryString !== newQueryString) {
        this.$router.push({ query: newQuery })
      }
    },

    /**
     * Handle dashboard sorting changes from self-managed ToolsToolbar
     * @param {Array} sorts - Array of { column, direction } objects
     */
    onDashboardSortingChange(sorts) {
      this.dashboardSortConfig = sorts || []
    },

    /**
     * Handle dashboard grouping changes from self-managed ToolsToolbar
     * @param {Array} groups - Array of groupBy property values
     */
    onDashboardGroupingChange(groups) {
      this.dashboardGroupByProperties = groups || []
    },

    /**
     * Handle dashboard column visibility changes from self-managed ToolsToolbar
     * @param {Array} updatedColumns - Updated array of column objects with visible property
     */
    onDashboardColumnsChange(updatedColumns) {
      this.dashboardColumns = updatedColumns.map((col) => ({
        text: col.text || col.columnName,
        value: col.value,
        visible: col.visible !== false,
      }))
    },

    /**
     * Scroll to a specific group in the table
     * @param {String} groupKey - The group key to scroll to
     */
    scrollToGroup(groupKey) {
      const group = this.filteredGroups.find((g) => g.key === groupKey)
      if (!group) return

      // Expand the group if collapsed
      if (this.showRows.indexOf(groupKey) === -1) {
        this.$store.commit('tools/showRows', groupKey)
      }

      // Wait for DOM update, then scroll
      this.$nextTick(() => {
        const element = document.getElementById(`group-${groupKey}`)
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    },

    /**
     * Handle clear all filters from FilterPanel
     */
    handleClearFilters() {
      this.localFilterData = {}
      this.localFilterKeys = []

      logger.console.info(['[Discrepancy] Filters cleared'])
    },

    /**
     * Handle grouping changes from ToolsDrawer
     * @param {Array} groupProperties - The new grouping properties
     */
    onGroupingChange(groupProperties) {
      logger.console.info(['[Discrepancy] Grouping changed to:', groupProperties])
      this.localGroupByProperties = [...(groupProperties || [])]
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * @param {Array} sortConfig - The new sorting configuration
     */
    onSortingChange(sortConfig) {
      logger.console.info(['[Discrepancy] Sorting changed to:', sortConfig])
    },

    exportAsCsv() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    exportAsXlsx() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

// ============================================================================
// COMPACT HEADER STYLES
// ============================================================================

.discrepancy-view-header-compact {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  .header-toolbar {
    border-bottom: none;
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(var(--v-primary-base), 0.08);
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-primary-base), 0.12);
    }
  }

  // Inline tabs styling
  .discrepancy-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px;
      background-color: transparent !important;
    }

    ::v-deep .v-tab {
      height: 56px;
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      padding: 0 16px;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tabs-slider {
      height: 3px;
      border-radius: 3px 3px 0 0;
    }
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }

  // Controls bar
  .controls-bar {
    border-radius: 0; // No border-radius since it's inside .tabs-section
    margin: 0; // No margin since it's inside .tabs-section

    .search-field-compact {
      ::v-deep .v-input__control {
        min-height: 38px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 38px !important;
        padding: 0 12px !important;
        border-radius: 6px;
        background-color: white;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.12);
      }

      .search-icon {
        color: rgba(0, 0, 0, 0.54);
        margin-right: 8px;
      }
    }

    // Button styling to match Time.vue
    .v-btn {
      text-transform: none;
      letter-spacing: 0.25px;
      font-weight: 500;
      height: 36px !important;
      border-radius: 6px;
    }
  }
}

// Dark theme support
.theme--dark .discrepancy-view-header-compact {
  background-color: #2d2d2d;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

  .header-toolbar {
    background-color: #2d2d2d !important;
  }

  .header-icon-wrapper {
    background: rgba(var(--v-primary-base), 0.15);

    &:hover {
      background: rgba(var(--v-primary-base), 0.2);
    }
  }

  .discrepancy-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      background-color: #2d2d2d !important;
    }

    ::v-deep .v-tab:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background-color: rgba(255, 255, 255, 0.05);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05);
      }

      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.12);
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.54);
      }
    }
  }
}

// Interactive states for search field
.discrepancy-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input--is-focused fieldset {
  border-color: var(--v-primary-base);
  border-width: 2px;
}

.discrepancy-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

.theme--dark .discrepancy-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input--is-focused fieldset {
  border-color: var(--v-primary-base);
  border-width: 2px;
}

.theme--dark .discrepancy-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Responsive adjustments
@media (max-width: 960px) {
  .discrepancy-view-header-compact {
    .header-toolbar {
      height: auto !important;
      min-height: 48px !important;
    }

    .header-icon-wrapper {
      width: 36px;
      height: 36px;
    }

    .discrepancy-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 48px !important;
      }

      ::v-deep .v-tab {
        height: 48px !important;
        font-size: 0.813rem;
        padding: 0 12px;
      }
    }
  }
}

@media (max-width: 768px) {
  .discrepancy-view-header-compact {
    .header-icon-wrapper {
      width: 32px;
      height: 32px;
      margin-right: 8px !important;
    }

    .discrepancy-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 44px !important;
      }

      ::v-deep .v-tab {
        height: 44px !important;
        font-size: 0.75rem;
        padding: 0 8px;
      }

      ::v-deep .v-tabs-slider {
        height: 2px;
      }
    }

    .controls-bar {
      .search-field-compact {
        ::v-deep .v-input__control {
          min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
          min-height: 36px !important;
        }
      }

      .v-btn {
        height: 30px !important;
        font-size: 0.813rem;
      }
    }
  }
}
</style>
